/**
 * features/setup/SubjectsSetup.tsx
 * Subject CRUD. Preloaded with the Cambridge/BEC subjects LKC offers
 * (incl. Setswana). `hasExam` drives whether a subject takes an exam
 * mark in Terms 2 & 3 (feature #8 wiring).
 */
import { useState, useMemo } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  writeBatch,
} from 'firebase/firestore';
// Batch B: undefined-safe write + save modal
// addDoc/updateDoc go through the safe wrapper; writeBatch (for the
// "seed starter subjects" path) keeps its existing code unchanged.
import { addDoc, updateDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { Plus, Pencil, Trash2, Search, Sparkles } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { sanitizeText } from '@/lib/sanitize';
import { Button, Card, PageHeader, Spinner } from '@/components/ui/primitives';
import { FormModal } from '@/components/ui/glass';
import type { Subject } from '@/types';
import {
  allDepartments as allDepts,
  departmentOfSubject,
  UNASSIGNED,
} from '@/lib/departments';

// Tile/badge tint per department (case-insensitive), grey fallback for
// anything outside LKC's known department set.
const DEPT_TONE: Record<string, string> = {
  mathematics: 'bg-primary/10 text-primary',
  science:
    'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  languages:
    'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  humanities:
    'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  ict: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  business: 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  arts: 'bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  'physical education':
    'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};
const DEPT_TONE_FALLBACK =
  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

// Department names match LKC's live department vocabulary (Teacher.department).
const SEED: Omit<Subject, 'id' | 'createdAt'>[] = [
  { name: 'English Language', shortName: 'Eng', department: 'Languages', hasExam: true },
  { name: 'English Literature', shortName: 'Eng Lit', department: 'Languages', hasExam: true },
  { name: 'Setswana', shortName: 'Tsw', department: 'Languages', hasExam: true },
  { name: 'Mathematics', shortName: 'Maths', department: 'Mathematics', hasExam: true },
  { name: 'Additional Mathematics', shortName: 'Add Maths', department: 'Mathematics', hasExam: true },
  { name: 'Biology', shortName: 'Bio', department: 'Science', hasExam: true },
  { name: 'Chemistry', shortName: 'Chem', department: 'Science', hasExam: true },
  { name: 'Physics', shortName: 'Phys', department: 'Science', hasExam: true },
  { name: 'Combined Science', shortName: 'Comb Sci', department: 'Science', hasExam: true },
  { name: 'Accounting', shortName: 'Acc', department: 'Business', hasExam: true },
  { name: 'Business Studies', shortName: 'Bus', department: 'Business', hasExam: true },
  { name: 'Economics', shortName: 'Econ', department: 'Business', hasExam: true },
  { name: 'History', shortName: 'Hist', department: 'Humanities', hasExam: true },
  { name: 'Geography', shortName: 'Geo', department: 'Humanities', hasExam: true },
  { name: 'Computer Science', shortName: 'CS', department: 'ICT', hasExam: true },
  { name: 'ICT', shortName: 'ICT', department: 'ICT', hasExam: true },
  { name: 'Design and Technology', shortName: 'D&T', department: 'Arts', hasExam: true },
  { name: 'Art and Design', shortName: 'Art', department: 'Arts', hasExam: true },
  { name: 'French', shortName: 'Fr', department: 'Languages', hasExam: true },
];

interface FormState {
  name: string;
  shortName: string;
  department: string;
  /** Cambridge IGCSE grading tier. 'core' grades C-G and caps at C. */
  gradeScheme: 'extended' | 'core';
  hasExam: boolean;
  displayOrder: number;
}

const EMPTY: FormState = {
  name: '',
  shortName: '',
  department: '',
  gradeScheme: 'extended',
  hasExam: true,
  displayOrder: 0,
};

export default function SubjectsSetup() {
  const { subjects, teachers, loading } = useData();
  const deptOptions = useMemo(
    () => allDepts(subjects, teachers),
    [subjects, teachers],
  );
  const { userData } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Subject | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Subject | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return [...subjects]
      .filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.shortName?.toLowerCase().includes(q),
      )
      .sort(
        (a, b) =>
          (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
          a.name.localeCompare(b.name),
      );
  }, [subjects, search]);

  function openAdd() {
    setEditing(null);
    setForm({ ...EMPTY, displayOrder: subjects.length });
    setShowModal(true);
  }

  function openEdit(s: Subject) {
    setEditing(s);
    setForm({
      name: s.name,
      shortName: s.shortName ?? '',
      department: s.department ?? '',
      gradeScheme: s.gradeScheme === 'core' ? 'core' : 'extended',
      hasExam: s.hasExam !== false,
      displayOrder: s.displayOrder ?? 0,
    });
    setShowModal(true);
  }

  async function save() {
    const name = sanitizeText(form.name, 80);
    if (!name) {
      setMsg({ ok: false, text: 'Subject name is required.' });
      return;
    }
    const department = sanitizeText(form.department, 40);
    if (!department) {
      setMsg({ ok: false, text: 'Please pick a department.' });
      return;
    }
    setBusy(true);
    setSaveModal({
      show: true,
      status: 'saving',
      message: editing ? 'Updating subject…' : 'Adding subject…',
    });
    try {
      // NOTE: `category` is intentionally NOT written. Existing docs keep
      // their category field (merge/update leaves it untouched); the app no
      // longer surfaces it — subjects are grouped by department instead.
      const payload = {
        name,
        shortName: sanitizeText(form.shortName, 20) || name.slice(0, 6),
        department,
        gradeScheme: form.gradeScheme,
        hasExam: form.hasExam,
        displayOrder: Number(form.displayOrder) || 0,
        updatedAt: new Date().toISOString(),
        updatedBy: userData?.uid,
      };
      if (editing) {
        await updateDoc(doc(db, COLLECTIONS.subjects, editing.id), payload);
        setSaveModal({ show: true, status: 'success', message: 'Subject updated.' });
      } else {
        await addDoc(collection(db, COLLECTIONS.subjects), {
          ...payload,
          createdAt: new Date().toISOString(),
          createdBy: userData?.uid,
        });
        setSaveModal({ show: true, status: 'success', message: 'Subject added.' });
      }
      setShowModal(false);
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function remove(s: Subject) {
    setBusy(true);
    try {
      await deleteDoc(doc(db, COLLECTIONS.subjects, s.id));
      setMsg({ ok: true, text: `Removed ${s.name}.` });
      setConfirmDelete(null);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function seedAll() {
    setBusy(true);
    try {
      const batch = writeBatch(db);
      SEED.forEach((s, i) => {
        const ref = doc(collection(db, COLLECTIONS.subjects));
        batch.set(ref, {
          ...s,
          displayOrder: i,
          createdAt: new Date().toISOString(),
          createdBy: userData?.uid,
        });
      });
      await batch.commit();
      setMsg({ ok: true, text: `Added ${SEED.length} starter subjects.` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner label="Loading subjects…" />;

  return (
    <div>
      <PageHeader
        title="Subjects"
        subtitle={`${subjects.length} subject${subjects.length === 1 ? '' : 's'} configured`}
        actions={
          <>
            {subjects.length === 0 && (
              <Button
                variant="gold"
                icon={<Sparkles size={16} />}
                loading={busy}
                onClick={seedAll}
              >
                Add LKC starter set
              </Button>
            )}
            <Button icon={<Plus size={16} />} onClick={openAdd}>
              Add subject
            </Button>
          </>
        }
      />

      {msg && (
        <div
          className={`mb-4 text-sm rounded-xl px-3 py-2.5 border ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {msg.text}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="relative max-w-xs">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search subjects…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-12">
            No subjects yet.
          </p>
        ) : (
          <div className="p-3 sm:p-4 space-y-2.5">
            {filtered.map((s) => {
              const dept = departmentOfSubject(s, teachers);
              const tone = DEPT_TONE[dept.toLowerCase()] ?? DEPT_TONE_FALLBACK;
              const badge = (
                s.shortName ||
                s.name.split(/\s+/).map((w) => w[0]).join('')
              )
                .slice(0, 3)
                .toUpperCase();
              return (
                <div
                  key={s.id}
                  className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all"
                >
                  <div className="flex items-center gap-4 p-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-sm shrink-0 ${tone}`}
                    >
                      {badge}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-800 dark:text-white truncate">
                        {s.name}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-0.5">
                        {s.shortName && (
                          <span>{s.shortName}</span>
                        )}
                        {dept && dept !== UNASSIGNED && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${tone}`}
                          >
                            {dept}
                          </span>
                        )}
                        {s.hasExam !== false ? (
                          <span className="text-emerald-600 font-medium">
                            ✓ exam
                          </span>
                        ) : (
                          <span className="text-gray-400">
                            CA only
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEdit(s)}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(s)}
                        className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Add/Edit modal */}
      <FormModal
        open={showModal}
        onClose={() => setShowModal(false)}
        icon={Sparkles}
        title={editing ? 'Edit subject' : 'Add subject'}
        maxWidth="max-w-md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowModal(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={save} loading={busy}>
              {editing ? 'Save changes' : 'Add subject'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Name</label>
                <input
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Short name</label>
                  <input
                    value={form.shortName}
                    onChange={(e) =>
                      setForm({ ...form, shortName: e.target.value })
                    }
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Department *</label>
                  <select
                    value={form.department}
                    onChange={(e) =>
                      setForm({ ...form, department: e.target.value })
                    }
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                  >
                    <option value="">Select department…</option>
                    {deptOptions
                      .filter((d) => d !== UNASSIGNED)
                      .map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-400 -mt-1">
                Department is required and drives how subjects are grouped
                across the app.
              </p>
              <div>
                <label className="text-sm font-medium">Grading scale</label>
                <select
                  value={form.gradeScheme}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      gradeScheme: e.target.value === 'core' ? 'core' : 'extended',
                    })
                  }
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                >
                  <option value="extended">Extended (A*-E)</option>
                  <option value="core">Core (C-G)</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Cambridge IGCSE tier. Core subjects grade C-G and are capped
                  at C (a mark of 85% grades C, never B).
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 items-center">
                <div>
                  <label className="text-sm font-medium">Display order</label>
                  <input
                    type="number"
                    value={form.displayOrder}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        displayOrder: Number(e.target.value),
                      })
                    }
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm mt-5">
                  <input
                    type="checkbox"
                    checked={form.hasExam}
                    onChange={(e) =>
                      setForm({ ...form, hasExam: e.target.checked })
                    }
                  />
                  Takes an exam (T2/T3)
                </label>
              </div>
        </div>
      </FormModal>


      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card glass className="w-full max-w-sm p-6 animate-fade-in">
            <h2 className="text-lg font-bold">Remove subject?</h2>
            <p className="text-sm text-gray-500 mt-2">
              This removes <strong>{confirmDelete.name}</strong>. Existing
              marks already saved are not deleted, but the subject will no
              longer be selectable.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="ghost"
                onClick={() => setConfirmDelete(null)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={busy}
                onClick={() => remove(confirmDelete)}
              >
                Remove
              </Button>
            </div>
          </Card>
        </div>
      )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );
}
